"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.simpleAgendaSlotApiSchema = exports.getAgendaSlotItemApiSchema = exports.agendaSlotDoctorAttendingSchema = exports.PRESENTIEL_SLOT_TYPE = exports.FULLVISIO_SLOT_TYPE = exports.agendaSlotTypeSchema = exports.agendaSlotTypes = void 0;
exports.isFullvisioSlotType = isFullvisioSlotType;
const zod_1 = require("zod");
const api_1 = require("../global/api");
const event_schema_1 = require("../global/event.schema");
exports.agendaSlotTypes = ['fullvisio', 'mixed'];
exports.agendaSlotTypeSchema = zod_1.z.enum(exports.agendaSlotTypes);
/**
 * The slot type used for remote (video) consultations. Remote doctors work
 * without a medical assistant, so a `fullvisio` slot is always "sans assistant"
 * (SA) regardless of the assistant planning.
 */
exports.FULLVISIO_SLOT_TYPE = 'fullvisio';
/**
 * The slot type used for on-site ("présentiel") consultations — the default.
 */
exports.PRESENTIEL_SLOT_TYPE = 'mixed';
/**
 * Single source of truth for the "remote ⇒ no assistant" rule.
 *
 * A full-video slot is performed remotely, so it never has a medical assistant.
 * Both the backend slot factory and the web assistant indicators derive the
 * SA/AA decision from this predicate, so the rule lives in one place.
 *
 * @param type - The slot type (`null`/`undefined` is treated as on-site).
 * @returns `true` when the slot is a remote full-video slot.
 */
function isFullvisioSlotType(type) {
    return type === exports.FULLVISIO_SLOT_TYPE;
}
exports.agendaSlotDoctorAttendingSchema = zod_1.z.object({
    id: zod_1.z.string().uuid(),
    type: (0, api_1.apiEntityTypeSchema)('doctor-attendings'),
    attributes: zod_1.z.object({
        firstname: zod_1.z.string(),
        lastname: zod_1.z.string(),
    }),
});
exports.getAgendaSlotItemApiSchema = zod_1.z.object({
    id: zod_1.z.string().uuid(),
    type: (0, api_1.apiEntityTypeSchema)('agenda-slots'),
    attributes: zod_1.z.object({
        startAt: zod_1.z.coerce.date(),
        endAt: zod_1.z.coerce.date(),
        periodOfTheDay: zod_1.z.enum(event_schema_1.periodsOfTheDay),
        type: exports.agendaSlotTypeSchema,
        rrule: event_schema_1.agendaRruleSchema,
        exdate: zod_1.z.array(zod_1.z.string()).optional().default([]),
        retrocessionRate: zod_1.z.number(),
        withAssistant: zod_1.z.boolean(),
        /**
         * True when `withAssistant` was not forced on the slot (null in DB) and was
         * instead inferred from the medical assistant planning at read time. Lets
         * the UI flag an auto-derived AA/SA mode apart from explicit DB overrides.
         */
        withAssistantInferred: zod_1.z.boolean().optional().default(false),
        /**
         * Explicit staff override: when true the slot is pinned "sans assistant"
         * (SA) regardless of the assistant planning. Lets the edit UI render and
         * toggle the override.
         */
        forceWithoutAssistant: zod_1.z.boolean().optional().default(false),
        sourceId: zod_1.z.string().uuid().nullable().default(null),
        occurenceIndex: zod_1.z.number().optional(),
        morningDoctolibDataCreatedAt: zod_1.z.coerce.date().nullable().default(null),
        workAtTheSameTime: zod_1.z
            .object({
            morning: zod_1.z.coerce.number(),
            afternoon: zod_1.z.coerce.number(),
            // Slots frozen before the night period existed have no night counter.
            night: zod_1.z.coerce.number().optional().default(0),
        })
            .nullable(),
        isFeeApplicable: zod_1.z.coerce.boolean().nullable(),
        createdAt: zod_1.z.coerce.date(),
        updatedAt: zod_1.z.coerce.date(),
        medicalAssistantWorkDuration: zod_1.z.number().optional(),
        medicalAssistantWorkPeriods: zod_1.z
            .array(zod_1.z.object({
            startTime: zod_1.z.coerce.date(),
            endTime: zod_1.z.coerce.date(),
            date: zod_1.z.coerce.date(),
        }))
            .optional(),
        /**
         * Computed medical-assistant work periods overlapping the slot (day-boundary
         * extended, pauses removed, Formation excluded), resolved server-side from
         * the assistant planning. Preferred over the legacy Skello
         * `medicalAssistantWorkPeriods`; empty when the slot has no assistant.
         */
        assistantPeriods: zod_1.z
            .array(zod_1.z.object({
            assistantId: zod_1.z.string().uuid(),
            userId: zod_1.z.string(),
            firstname: zod_1.z.string(),
            lastname: zod_1.z.string(),
            startTime: zod_1.z.string(),
            endTime: zod_1.z.string(),
            durationMinutes: zod_1.z.number(),
        }))
            .optional(),
        reconciliationWarningType: zod_1.z.string().nullable().optional(),
        pendingRequestCount: zod_1.z.number().optional().default(0),
        availableSubstitutesCount: zod_1.z.number().optional().default(0),
        pendingInvitation: zod_1.z
            .object({
            firstName: zod_1.z.string(),
            lastName: zod_1.z.string(),
            email: zod_1.z.string(),
        })
            .nullable()
            .default(null),
    }),
    relationships: zod_1.z
        .object({
        doctorAttending: (0, api_1.apiEntityRelationshipSchema)('doctor-attendings').optional(),
        reservations: (0, api_1.apiEntityRelationshipsSchema)('agenda-slot-reservations').optional(),
    })
        .optional()
        .default({}),
});
exports.simpleAgendaSlotApiSchema = zod_1.z.object({
    id: zod_1.z.string().uuid(),
    type: (0, api_1.apiEntityTypeSchema)('agenda-slots'),
    attributes: zod_1.z.object({
        startAt: zod_1.z.coerce.date(),
        endAt: zod_1.z.coerce.date(),
        periodOfTheDay: zod_1.z.enum(event_schema_1.periodsOfTheDay),
        type: exports.agendaSlotTypeSchema,
    }),
});
